import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, ExternalLink, Phone, Mail, Star, Search, Filter, Badge, Accessibility, ArrowUpDown, Lock, Crown, Eye, EyeOff, Sparkles, ChevronRight, Users, Heart } from "lucide-react";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: "alphabetical", label: "A-Z", icon: "🔤" },
  { value: "area", label: "By Area", icon: "📍" },
  { value: "activity", label: "By Activity", icon: "🎯" },
];

// Number of free preview venues per category for guests
const FREE_PREVIEW_COUNT = 3;

const VenuesDirectory = () => {
  const [venues, setVenues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchCity, setSearchCity] = useState("");
  const [partnerOnly, setPartnerOnly] = useState(false);
  const [sortBy, setSortBy] = useState("alphabetical");
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(false);
  
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [user, setUser] = useState(null);
  const [userTier, setUserTier] = useState("guest"); // guest, free, premium

  useEffect(() => {
    fetchCategories();
    fetchVenues();
    if (token) {
      fetchUserProfile();
    }
  }, []);

  useEffect(() => {
    fetchVenues();
  }, [selectedCategory, searchCity, partnerOnly]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setUserTier(response.data.is_premium ? "premium" : "free");
    } catch (error) {
      console.error("Error fetching user:", error);
      setUserTier("guest");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/venues/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchVenues = async () => {
    try {
      setLoading(true);
      let url = `${API}/venues?limit=100`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (searchCity) url += `&city=${encodeURIComponent(searchCity)}`;
      if (partnerOnly) url += `&partner_only=true`;
      
      const response = await axios.get(url);
      setVenues(response.data.venues || []);
    } catch (error) {
      console.error("Error fetching venues:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryEmoji = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.emoji || "📍";
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  // Sort venues
  const sortedVenues = [...venues].sort((a, b) => {
    switch (sortBy) {
      case "alphabetical":
        return (a.name || "").localeCompare(b.name || "");
      case "area":
        return (a.city || "").localeCompare(b.city || "");
      case "activity":
        return (getCategoryName(a.category) || "").localeCompare(getCategoryName(b.category) || "");
      default:
        return 0;
    }
  });

  // Apply access control - guests see limited venues
  const getAccessibleVenues = () => {
    if (userTier === "premium" || userTier === "free") {
      return sortedVenues;
    }
    // Guests only see first FREE_PREVIEW_COUNT venues per category
    const categoryCount = {};
    return sortedVenues.filter(venue => {
      const cat = venue.category || "other";
      categoryCount[cat] = (categoryCount[cat] || 0) + 1;
      return categoryCount[cat] <= FREE_PREVIEW_COUNT;
    });
  };

  const accessibleVenues = getAccessibleVenues();
  const lockedCount = sortedVenues.length - accessibleVenues.length;

  // Group venues
  const getGroupedVenues = () => {
    const venuesToGroup = accessibleVenues;
    if (sortBy === "area") {
      const grouped = {};
      venuesToGroup.forEach(venue => {
        const key = venue.city || "Unknown Area";
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(venue);
      });
      return grouped;
    }
    if (sortBy === "activity") {
      const grouped = {};
      venuesToGroup.forEach(venue => {
        const key = getCategoryName(venue.category) || "Other";
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(venue);
      });
      return grouped;
    }
    const grouped = {};
    venuesToGroup.forEach(venue => {
      const key = (venue.name || "?")[0].toUpperCase();
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(venue);
    });
    return grouped;
  };

  const groupedVenues = getGroupedVenues();

  // Check if venue details should be blurred (guest trying to see full details)
  const shouldBlurDetails = (venue) => {
    return userTier === "guest";
  };

  // Check if SEN features should be locked (non-premium)
  const shouldLockSEN = () => {
    return userTier !== "premium";
  };

  const handleLockedClick = (type) => {
    if (type === "details") {
      setShowSignupPrompt(true);
    } else if (type === "sen") {
      setShowPremiumPrompt(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                🎪 Family Venues Directory
              </h1>
              <p className="text-white/90">
                Discover amazing places to visit with your family
              </p>
            </div>
            {userTier === "guest" && (
              <div className="hidden md:block bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-sm font-medium">🔓 Sign up free to unlock all venues</p>
              </div>
            )}
          </div>
          
          {/* Access Level Indicator */}
          <div className="mt-4 flex flex-wrap gap-2">
            {userTier === "guest" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 rounded-full text-sm">
                <Eye size={14} /> Preview Mode - {FREE_PREVIEW_COUNT} venues per category
              </span>
            )}
            {userTier === "free" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-400/30 rounded-full text-sm">
                <Users size={14} /> Free Member - Full Access + SEN Features
              </span>
            )}
            {userTier === "premium" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-400/30 rounded-full text-sm">
                <Crown size={14} /> Premium - Full Access + Exclusive Perks
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Signup Prompt Modal */}
      {showSignupPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSignupPrompt(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Unlock the Full Directory
              </h3>
              <p className="text-gray-600">
                Create a free account to access all {venues.length}+ venues, full details, and SEN accessibility info!
              </p>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600">✓</span>
                Full venue details & addresses
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600">✓</span>
                Contact information & websites
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center text-teal-600">♿</span>
                <span className="font-medium">SEN accessibility filters & info</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600">✓</span>
                User reviews & ratings
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600">✓</span>
                Save favourites & create lists
              </div>
            </div>
            
            <div className="space-y-2">
              <Button onClick={() => navigate("/register")} className="w-full bg-gradient-to-r from-purple-600 to-pink-500">
                Sign Up Free <ChevronRight size={16} />
              </Button>
              <Button variant="outline" onClick={() => navigate("/login")} className="w-full">
                Already have an account? Log in
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Prompt Modal */}
      {showPremiumPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPremiumPrompt(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Premium Member Perks
              </h3>
              <p className="text-gray-600">
                Upgrade for exclusive discounts and member-only benefits
              </p>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">💰</span>
                Exclusive partner venue discounts
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">💎</span>
                Hidden gems & secret spots
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">⭐</span>
                Member-verified reviews
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">🎟️</span>
                Early access to events & meetups
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">📋</span>
                Printable day-out planners
              </div>
            </div>
            
            <Button onClick={() => navigate("/subscription")} className="w-full bg-gradient-to-r from-yellow-400 to-orange-500">
              Upgrade to Premium <Crown size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search by city (e.g., London, Croydon)"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant={partnerOnly ? "default" : "outline"}
            onClick={() => setPartnerOnly(!partnerOnly)}
            className="whitespace-nowrap"
          >
            <Star size={16} className={partnerOnly ? "fill-current" : ""} />
            <span className="ml-2 hidden sm:inline">Partners Only</span>
          </Button>
        </div>

        {/* Sort Options */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-sm text-gray-600 font-medium flex items-center gap-1">
            <ArrowUpDown size={14} /> Sort by:
          </span>
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                sortBy === option.value
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-purple-100"
              }`}
            >
              {option.icon} {option.label}
            </button>
          ))}
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === null
                ? "bg-purple-600 text-white"
                : "bg-white text-gray-700 hover:bg-purple-100 border border-gray-200"
            }`}
          >
            All Venues
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat.id
                  ? "bg-purple-600 text-white"
                  : "bg-white text-gray-700 hover:bg-purple-100 border border-gray-200"
              }`}
            >
              {cat.emoji} {cat.name}
            </button>
          ))}
        </div>

        {/* Results Count with Lock Info */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-600">
            {loading ? "Loading..." : (
              <>
                Showing {accessibleVenues.length} of {venues.length} venues
                {lockedCount > 0 && userTier === "guest" && (
                  <button 
                    onClick={() => setShowSignupPrompt(true)}
                    className="ml-2 text-purple-600 font-medium hover:text-purple-700"
                  >
                    🔒 Unlock {lockedCount} more
                  </button>
                )}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Premium SEN Filter Bar - Available for Free Members and Premium */}
      {(userTier === "premium" || userTier === "free") && (
        <div className="max-w-6xl mx-auto px-4 mb-4">
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-200">
            <h4 className="font-semibold text-teal-800 mb-2 flex items-center gap-2">
              <Accessibility size={16} /> SEN Accessibility Filters
            </h4>
            <div className="flex flex-wrap gap-2">
              <button className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-sm font-medium hover:bg-teal-200">
                🔇 Quiet Hours Available
              </button>
              <button className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-sm font-medium hover:bg-teal-200">
                ♿ Changing Places
              </button>
              <button className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-sm font-medium hover:bg-teal-200">
                🎧 Sensory Friendly
              </button>
              <button className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-sm font-medium hover:bg-teal-200">
                🅿️ Blue Badge Parking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SEN Filter Teaser - For Guests Only (encourages signup) */}
      {userTier === "guest" && (
        <div className="max-w-6xl mx-auto px-4 mb-4">
          <button 
            onClick={() => setShowSignupPrompt(true)}
            className="w-full bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-200 text-left hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-teal-800 mb-1 flex items-center gap-2">
                  <Accessibility size={16} /> SEN Accessibility Filters
                </h4>
                <p className="text-sm text-teal-600">
                  Sign up free to filter by quiet hours, changing places, sensory-friendly & more
                </p>
              </div>
              <Lock className="w-6 h-6 text-teal-400" />
            </div>
          </button>
        </div>
      )}

      {/* Premium Extras Banner - For Free Members */}
      {userTier === "free" && (
        <div className="max-w-6xl mx-auto px-4 mb-4">
          <button 
            onClick={() => setShowPremiumPrompt(true)}
            className="w-full bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-200 text-left hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-yellow-800 mb-1 flex items-center gap-2">
                  <Crown size={16} className="text-yellow-500" /> Unlock Premium Perks
                </h4>
                <p className="text-sm text-yellow-700">
                  Exclusive discounts, hidden gems, member reviews & more
                </p>
              </div>
              <Sparkles className="w-6 h-6 text-yellow-400" />
            </div>
          </button>
        </div>
      )}

      {/* Venues Grid */}
      <div className="max-w-6xl mx-auto px-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : venues.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No venues found. Try adjusting your filters!</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedVenues).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, groupVenues]) => (
              <div key={groupName}>
                {/* Group Header */}
                <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl px-4 py-2 mb-4 shadow-sm">
                  <h3 className="font-bold text-purple-800 flex items-center gap-2">
                    {sortBy === "area" && <MapPin size={16} />}
                    {sortBy === "activity" && <span>🎯</span>}
                    {sortBy === "alphabetical" && <span className="text-lg">{groupName}</span>}
                    {sortBy !== "alphabetical" && groupName}
                    <span className="text-sm font-normal text-purple-600">({groupVenues.length})</span>
                  </h3>
                </div>
                
                {/* Venues in this group */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {groupVenues.map((venue) => (
                    <div
                      key={venue.venue_id}
                      className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-lg transition-all"
                    >
                      {/* Category Header */}
                      <div className="bg-gradient-to-r from-purple-100 to-pink-100 px-4 py-3 flex items-center justify-between">
                        <span className="text-2xl">{getCategoryEmoji(venue.category)}</span>
                        <div className="flex items-center gap-2">
                          {venue.is_partner && (
                            <span className="bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                              <Star size={12} className="fill-current" /> Partner
                            </span>
                          )}
                          {venue.partner_discount && userTier === "premium" && (
                            <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                              {venue.partner_discount}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: "Fredoka, sans-serif" }}>
                          {venue.name}
                        </h3>
                        <p className="text-sm text-purple-600 font-medium mb-2">
                          {getCategoryName(venue.category)}
                        </p>
                        
                        {venue.description && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {venue.description}
                          </p>
                        )}

                        {/* Location - Blurred for guests */}
                        <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
                          <MapPin size={16} className="text-pink-500 mt-0.5 flex-shrink-0" />
                          {shouldBlurDetails(venue) ? (
                            <button 
                              onClick={() => handleLockedClick("details")}
                              className="text-gray-400 hover:text-purple-600 flex items-center gap-1"
                            >
                              <span className="blur-sm select-none">123 Example Street</span>
                              <Lock size={12} />
                            </button>
                          ) : (
                            <span>{venue.city}{venue.postcode && `, ${venue.postcode}`}</span>
                          )}
                        </div>

                        {/* Age Range */}
                        {venue.age_range && (
                          <div className="text-sm text-gray-500 mb-3">
                            👶 Ages: {venue.age_range}
                          </div>
                        )}

                        {/* Accessibility Features - Available for Free and Premium members */}
                        {venue.accessibility_features?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {userTier === "guest" ? (
                              // Guests see teaser
                              <button 
                                onClick={() => setShowSignupPrompt(true)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-600 rounded-full text-xs hover:bg-teal-100"
                              >
                                <Accessibility size={10} /> {venue.accessibility_features.length} accessibility features - sign up to view
                              </button>
                            ) : (
                              // Free and Premium see all features
                              venue.accessibility_features.map((feature, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs"
                                >
                                  <Accessibility size={10} /> {feature}
                                </span>
                              ))
                            )}
                          </div>
                        )}

                        {/* Actions - Partially locked for guests */}
                        <div className="flex gap-2 pt-3 border-t border-gray-100">
                          {shouldBlurDetails(venue) ? (
                            <button
                              onClick={() => handleLockedClick("details")}
                              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm font-medium hover:bg-purple-100 hover:text-purple-700 transition-colors"
                            >
                              <Lock size={14} /> Sign up to view
                            </button>
                          ) : (
                            <>
                              {venue.website && (
                                <a
                                  href={venue.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition-colors"
                                >
                                  <ExternalLink size={14} /> Website
                                </a>
                              )}
                              {venue.phone && (
                                <a
                                  href={`tel:${venue.phone}`}
                                  className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
                                >
                                  <Phone size={14} />
                                </a>
                              )}
                              {venue.email && (
                                <a
                                  href={`mailto:${venue.email}`}
                                  className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                                >
                                  <Mail size={14} />
                                </a>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Locked Venues Teaser */}
            {lockedCount > 0 && userTier === "guest" && (
              <div className="mt-8">
                <button 
                  onClick={() => setShowSignupPrompt(true)}
                  className="w-full bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-8 text-center hover:shadow-lg transition-all border-2 border-dashed border-purple-300"
                >
                  <Lock className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-purple-800 mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                    {lockedCount} More Venues Waiting!
                  </h3>
                  <p className="text-purple-600 mb-4">
                    Create a free account to unlock the full directory
                  </p>
                  <span className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full font-bold">
                    Sign Up Free <ChevronRight size={16} />
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Partner CTA */}
      <div className="max-w-6xl mx-auto px-4 mt-8">
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-6 text-white text-center">
          <h3 className="text-xl font-bold mb-2">Own a Family Venue?</h3>
          <p className="text-white/90 mb-4">
            Partner with KiddiPlay and reach thousands of local families!
          </p>
          <Link
            to="/partners"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-full font-bold hover:bg-purple-50 transition-colors"
          >
            Become a Partner <Star size={16} className="fill-current" />
          </Link>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default VenuesDirectory;
